/**
 * PASST DER KOPF IN EINE ZEILE? (Rafael, 03.10.2026 15:39: „alles in eine Leiste quetschen? …
 * oder in zwei"). Gemessen statt geschaetzt: Die Bedienung traegt je nach Lage Warnungen,
 * Zaehler und den Benutzer-Chip; eine feste Bildschirmbreite als Grenze waere einmal zu frueh,
 * einmal zu spaet. Gerechnet wird die NATUERLICHE Breite jedes Abschnitts (Summe seiner
 * Kinder samt Abstaenden) — unabhaengig davon, wie die Abschnitte gerade verteilt sind, damit
 * die Entscheidung nicht zwischen den beiden Formen hin und her springt.
 */
import { useLayoutEffect, useState, type RefObject } from 'react';

/** Natuerliche Breite eines Abschnitts in Layout-Pixeln (offsetWidth, wie clientWidth des Behaelters). */
export function natuerlicheBreite(el: HTMLElement): number {
  const cs = getComputedStyle(el);
  const rand = parseFloat(cs.paddingLeft) + parseFloat(cs.paddingRight) + parseFloat(cs.borderLeftWidth) + parseFloat(cs.borderRightWidth);
  // KI-Leiste: Raster aus gleich grossen Boxen — in einer Zeile = n Boxen + Abstaende.
  const boxen = el.classList.contains('ki-leiste') ? [...el.querySelectorAll<HTMLElement>('.ki-leiste-zugang')] : null;
  if (boxen?.length) {
    const knopf = el.querySelector<HTMLElement>('.ki-leiste-knopf');
    if (!knopf) return el.offsetWidth;
    const k = getComputedStyle(knopf);
    const abstand = parseFloat(k.columnGap) || 0;
    // Reserve, Fokuszeit und Kalender stehen neben den Kontenboxen. Ihre
    // Breite gehoert ebenfalls in die Entscheidung, ob die Kopfzeile passt.
    let zusatzBreite = 0, zusatzAnzahl = 0;
    for (const kind of el.children) {
      if (!(kind instanceof HTMLElement) || kind === knopf || kind.contains(knopf)) continue;
      const z = getComputedStyle(kind);
      if (z.display === 'none' || z.position === 'absolute' || z.position === 'fixed') continue;
      zusatzBreite += kind.offsetWidth + (parseFloat(z.marginLeft) || 0) + (parseFloat(z.marginRight) || 0);
      zusatzAnzahl += 1;
    }
    return rand + parseFloat(k.paddingLeft) + parseFloat(k.paddingRight)
      + boxen.reduce((s, b) => s + b.offsetWidth, 0) + abstand * (boxen.length - 1)
      + zusatzBreite + (parseFloat(cs.columnGap) || 0) * zusatzAnzahl;
  }
  const abstand = parseFloat(cs.columnGap) || 0;
  let summe = 0, anzahl = 0;
  for (const kind of el.children) {
    if (!(kind instanceof HTMLElement) || kind.classList.contains('cui-kopf-feder')) continue;
    const k = getComputedStyle(kind);
    if (k.display === 'none' || k.position === 'absolute' || k.position === 'fixed') continue;
    summe += kind.offsetWidth + parseFloat(k.marginLeft) + parseFloat(k.marginRight);
    anzahl += 1;
  }
  return rand + summe + abstand * Math.max(0, anzahl - 1);
}

export function passtInEineZeile(breiten: number[], verfuegbar: number): boolean {
  return verfuegbar > 0 && breiten.reduce((s, b) => s + b, 0) <= verfuegbar;
}

export function useKopfEinzeilig(ref: RefObject<HTMLElement | null>): boolean {
  const [einzeilig, setEinzeilig] = useState(false);
  useLayoutEffect(() => {
    const zeile = ref.current;
    if (!zeile) return;
    let frame = 0;
    const pruefe = () => {
      frame = 0;
      const abschnitte = [...zeile.children].filter((k): k is HTMLElement => k instanceof HTMLElement && getComputedStyle(k).display !== 'none');
      setEinzeilig(passtInEineZeile(abschnitte.map(natuerlicheBreite), zeile.clientWidth));
    };
    const bald = () => { if (!frame) frame = requestAnimationFrame(pruefe); };
    pruefe();
    // Ohne ResizeObserver (jsdom in Tests) bleibt es bei der einen Messung — jeder Browser der CUI hat ihn.
    if (typeof ResizeObserver === 'undefined') return () => { if (frame) cancelAnimationFrame(frame); };
    const ro = new ResizeObserver(bald);
    ro.observe(zeile);
    for (const k of zeile.children) ro.observe(k);
    // Warnungen, Zaehler und Zugaenge kommen und gehen — jede Aenderung im Kopf neu messen.
    const mo = new MutationObserver(() => { for (const k of zeile.children) ro.observe(k); bald(); });
    mo.observe(zeile, { childList: true, subtree: true, characterData: true });
    document.fonts?.ready.then(bald).catch(() => {});
    return () => { ro.disconnect(); mo.disconnect(); if (frame) cancelAnimationFrame(frame); };
  }, [ref]);
  return einzeilig;
}
